import React, { useEffect, useRef, useState } from "react";

/**
 * Live application health dot.
 *
 * Polls `/api/health` every `intervalMs` (default 30 s) and reflects the
 * result via the existing breathing `.presence-dot` style:
 *
 *   • ok        → green breathing dot (system is up + DB reachable)
 *   • degraded  → amber breathing dot (API up, DB ping failed)
 *   • down      → red breathing dot (API unreachable / non-2xx)
 *   • checking  → muted static dot (initial probe still in flight)
 *
 * Real-time guarantees:
 *   - First probe fires immediately on mount, not after the first interval.
 *   - Browser-tab visibility is honoured: while the tab is hidden we stop
 *     polling so a 10-tab user doesn't fan out probes pointlessly; on
 *     re-focus we fire an immediate probe and resume the interval.
 *   - In-flight probes are aborted on unmount so the component is safe to
 *     remount during route changes.
 *
 * Props
 *   intervalMs   polling interval in ms (default 30_000)
 *   compact      smaller layout (dot + 1-word label) for sidebar use
 *   className    Tailwind classes appended to the outer span
 *
 * The component is intentionally framework-agnostic — it speaks fetch
 * directly (no axios) so the Landing / Login pages can mount it before
 * the AuthContext is wired up.
 */

const STATUS = {
  CHECKING: { label: "Checking",  cls: "presence-dot--checking", tone: "text-muted-foreground" },
  OK:       { label: "Online",    cls: "",                       tone: "text-emerald-500" },
  DEGRADED: { label: "Degraded",  cls: "presence-dot--degraded", tone: "text-amber-500" },
  DOWN:     { label: "Offline",   cls: "presence-dot--down",     tone: "text-rose-500" },
};

function backendUrl() {
  // Mirror /lib/api.js conventions: always use REACT_APP_BACKEND_URL so the
  // dot works identically in preview and production environments.
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base.replace(/\/$/, "")}/api/health`;
}

export default function AppStatusDot({ intervalMs = 30_000, compact = false, className = "" }) {
  const [state, setState] = useState(STATUS.CHECKING);
  const [lastChecked, setLastChecked] = useState(null);
  const inflightRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const probe = async () => {
      // Cancel any previous still-running fetch — prevents stacking when
      // the user toggles tab visibility rapidly.
      if (inflightRef.current) inflightRef.current.abort();
      const ctrl = new AbortController();
      inflightRef.current = ctrl;
      try {
        const r = await fetch(backendUrl(), {
          signal: ctrl.signal,
          credentials: "include",
          cache: "no-store",
        });
        if (cancelled) return;
        if (!r.ok) { setState(STATUS.DOWN); }
        else {
          const j = await r.json().catch(() => ({}));
          if (j.status === "ok") setState(STATUS.OK);
          else if (j.status === "degraded") setState(STATUS.DEGRADED);
          else setState(STATUS.DOWN);
        }
      } catch (e) {
        if (cancelled || e?.name === "AbortError") return;
        setState(STATUS.DOWN);
      } finally {
        if (!cancelled) setLastChecked(new Date());
        if (inflightRef.current === ctrl) inflightRef.current = null;
      }
    };

    const schedule = () => {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(probe, intervalMs);
    };

    const onVisibility = () => {
      if (document.hidden) {
        clearInterval(timerRef.current);
      } else {
        probe();        // immediate refresh on re-focus
        schedule();
      }
    };

    probe();
    schedule();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      inflightRef.current?.abort();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);

  const tooltip = lastChecked
    ? `App status: ${state.label} · last checked ${new Intl.DateTimeFormat("en-AU", {
        timeZone: "Australia/Sydney",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
        timeZoneName: "short",
      }).format(lastChecked)}`
    : `App status: ${state.label}`;

  return (
    <span
      data-testid="app-status-dot"
      data-status={state.label.toLowerCase()}
      className={`inline-flex items-center gap-2 ${className}`}
      title={tooltip}
      role="status"
      aria-live="polite"
    >
      <span className={`presence-dot flex-shrink-0 ${state.cls}`} aria-hidden="true" />
      <span className={`label-mono ${state.tone}`}>
        {compact ? state.label : `App ${state.label}`}
      </span>
    </span>
  );
}
